import { IPaginator } from 'models/paginator';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import { IAncillaryDetails } from './ancillaries';
import { ILicenceSafeStorage } from './licenceConfigurator';
import { ICustomFieldByObjectType } from './customField';
import { ICustomFieldForm } from 'pages/LegalEntitiesPage/IndividualSearchForm';

export interface ILegalEntityResponse {
  uniqueId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  genderUniqueId: string;
  visibilityUniqueId: string;
  city: string;
}

export interface ILegalEntity {
  uniqueId: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  genderUniqueId: string;
  visibilityUniqueId: string | null;
  prefixGroupUniqueId: string | null;
  city: string | null;
  customFields?: ILegalEntityCustomField[];
  displayUniqueId: string;
  identityDocumentNumber?: string;
  primaryWeaponLicencingAuthorityUniqueId?: string;
  secondaryWeaponLicencingAuthorityUniqueId?: string;
}

export interface ILegalEntityFirearm {
  uniqueId: string;
  serialNumber: string;
  fireCycleId: string;
  makeUniqueId: string;
  modelUniqueId: string;
  caliberUniqueId: string;
  stateUniqueId: string;
  activityUniqueId: string;
  legalityUniqueId: string;
  keeper: string;
  owner: string;
  exhibitNumber: string;
  manufacturerUniqueId: string;
  groupName: string;
  showNoActiveLicenceWarning: boolean;
  showNoOwnerActiveLicenceWarning: boolean;
  showNoKeeperActiveLicenceWarning: boolean;
  showExpiredLinkedWarning: boolean;
  showInactiveOwnerOrKeeperWarning: boolean;
}

export interface ILegalEntityGroup {
  uniqueId: string;
  name: string;
  owner: string;
  keeper: string;
  firearmAmount: number;
  state: string | null;
  legalityUniqueId: string | null;
  activityUniqueId: string | null;
  stateUniqueId: string | null;
  hasDifferentStates: boolean;
  licenceAuthorityIds: string[];
  latitude: number | null;
  longitude: number | null;
}

export interface ILegalEntityGroupResponse {
  uniqueId: string;
  name: string;
  firearmIds: string[];
  legalEntityGroup: number;
  firearmAmount: number;
  keeper: string;
  keeperUniqueId: string;
  keeperType: string;
  keeperVisibilityUniqueId: string;
  owner: string;
  ownerUniqueId: string;
  ownerType: string;
  ownerVisibilityUniqueId: string;
  hasDifferentStates: boolean;
  stateUniqueId?: string;
  licenceAuthorityIds: string[];
}

export interface ILegalEntityAncillaryGroupAddResponse {
  uniqueId: string;
  name: string;
  ancillariesIds: string[];
}

export interface ILegalEntityLicenceAuthorityResponse {
  primaryLicenceAuthorityUniqueId: string;
  secondaryLicenceAuthorityUniqueId: string;
}

export interface ILegalEntityListResponse extends IPaginator {
  content: ILegalEntity[];
}

export interface ILegalEntityFirearmListResponse extends IPaginator {
  content: ILegalEntityFirearm[];
}

export interface ILegalEntityGroupListResponse extends IPaginator {
  content: ILegalEntityGroup[];
}

export interface ILegalEntityType {
  type: 'INDIVIDUAL' | 'ORGANIZATION';
}

export const mapILegalEntityTypeToELegalEntityTypes = (val: string | null | undefined) => {
  if (!val) {
    return null;
  }
  // eslint-disable-next-line default-case
  switch (val.toUpperCase()) {
    case LETypes.individual: {
      return ELegalEntityTypes.individual;
    }
    case LETypes.organization: {
      return ELegalEntityTypes.organisation;
    }
    default:
      return null;
  }
};

export interface ILegalEntityAncillaries {
  uniqueId: string;
  serialNumber: string;
  activityUniqueId: string;
  legalityUniqueId: string;
  fireCycleId: string;
  stateUniqueId: string;
  keeperId: string;
  keeperName: string;
  ancillaryDictionaryUniqueId: string;
  ancillaryDictionaryValue: string;
  manufacturerUniqueId: string;
  calibreUniqueId: string;
  ancillaryType: string;
  ancillaryTypeUniqueId: string;
  frtTypeUniqueId: string;
  groupName: string;
  showInactiveOwnerOrKeeperWarning: boolean;
}

export interface ILegalEntityAncillariesListResponse extends IPaginator {
  content: ILegalEntityAncillaries[];
}

export interface ILegalEntityAncillaryGroupAncillaries {
  ancillaries: IAncillaryDetails[];
  hasDifferentAncillaryTypes: boolean;
  groupName: string;
  artId: string;
  artSpecificType: string;
  artType: string;
  artTypeUniqueId: string;
  artUniqueId: string;
  calibre: string;
  manufacturer: string;
}

export interface ILegalEntityAncillaryGroup {
  uniqueId: string;
  name: string;
  state: string;
  keeper: string;
  keeperId: string;
  keeperType: string;
  keeperVisibilityUniqueId: string;
  owner: string;
  ownerId: string;
  ownerType: string;
  ownerVisibilityUniqueId: string;
  ancillaryAmount: string;
  legalityUniqueId: string;
  activityUniqueId: string;
  stateUniqueId: string;
  hasDifferentStates: boolean;
  licenceAuthorityIds: string[];
}

export interface ILegalEntityAncillaryGroupResponse extends IPaginator {
  content: ILegalEntityAncillaryGroup[];
}
export interface IOrgLegalEntityResponse {
  uniqueId: string;
  organizationName: string;
  organisationTypeUniqueIds: string;
  organisationCategoryUniqueId: string;
  departmentName: string;
  visibilityUniqueId: string;
  city: string;
}

export interface ILegalEntityAddress {
  uniqueId: string;
  typeUniqueId: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  regionUniqueId: string;
  countryUniqueId: string;
  latitude: number | string;
  longitude: number | string;
  isDefault: boolean;
  storage: boolean;
}

export interface ILegalEntityExtendedAddress {
  uniqueId: string;
  typeUniqueId: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  regionUniqueId: string;
  countryUniqueId: string;
  latitude: number | string;
  longitude: number | string;
  isDefault: boolean;
  storage: boolean;
  firearmAmount?: EAddressAssetAmount;
  ancillaryAmount?: EAddressAssetAmount;
}

export interface ILegalEntityAddressDetailFirearms {
  uniqueId: string;
  serialNumber: string;
  fireCycleId: string;
  make: string;
  model: string;
  stateUniqueId: string;
  legalityUniqueId: string;
  activityUniqueId: string;
}
export interface ILegalEntityAddressDetailAccessories {
  uniqueId: string;
  serialNumber: string;
  fireCycleId: string;
  accessoryTypeUniqueId: string;
  hostTypeUniqueId: string;
  stateUniqueId: string;
  legalityUniqueId: string;
  activityUniqueId: string;
}
export interface ILegalEntityAddressDetailAmmunition {
  uniqueId: string;
  fireCycleId: string;
  lotNumber: string;
  batchNumber: string;
  ammunitionTypeUniqueId: string;
  calibreUniqueId: string;
  manufacturerUniqueId: string;
  stateUniqueId: string;
  legalityUniqueId: string;
  activityUniqueId: string;
}
export interface ILegalEntityAddressDetailComponents {
  uniqueId: string;
  serialNumber: string;
  fireCycleId: string;
  componentTypeUniqueId: string;
  frtTypeUniqueId: string;
  frtMakeUniqueId: string;
  frtModelUniqueId: string;
  stateUniqueId: string;
  legalityUniqueId: string;
  activityUniqueId: string;
}
export interface ILegalEntityAddressDetailLicence {
  weaponLicenceName: string;
  weaponLicenceNumber: string;
  legalUniqueId: string;
  legalTypeUniqueId: string;
  statusUniqueId: string;
  expiryDateFrom: string;
  expiryDateTo: string;
  legalName: string;
}

export interface ILegalEntityAddressDetail {
  firearms: ILegalEntityAddressDetailFirearms[];
  accessories: ILegalEntityAddressDetailAccessories[];
  ammunition: ILegalEntityAddressDetailAmmunition[];
  components: ILegalEntityAddressDetailComponents[];
  licences?: ILegalEntityAddressDetailLicence[];
}

export enum EAssetType {
  firearms = 'firearms',
  accessories = 'accessories',
  ammunition = 'ammunition',
  components = 'components',
  licences = 'licences',
}

export enum EAddressAssetAmount {
  NONE = 'NONE',
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE',
}

export interface ILegalEntityContact {
  uniqueId: string;
  name?: string;
  role?: string;
  email: string;
  phoneNumber: {
    codeDictionaryUniqueId: string;
    value: string;
  } | null;
  associatedIndividualUniqueId?: string;
  associatedIndividualName?: string;
}

export interface ILegalEntityContactWithAssociation {
  uniqueId: string;
  name?: string;
  role?: string;
  email: string;
  legalContactType?: string;
  phoneNumber: {
    codeDictionaryUniqueId: string;
    value: string;
  };
  canEdit?: boolean;
  assignedIndividualUniqueId?: string;
  assignedOrganisationUniqueId?: string;
  assignedOrganisationName?: string;
  assignedIndividualPrefixGroupUniqueId?: string;
  assignedIndividualVisibilityUniqueId?: string;
  assignedOrganisationVisibilityUniqueId?: string;
  assignedOrganisationCategoryUniqueId?: string;
}
export interface ILegalEntityPreviousNamesSurnames {
  uniqueId: string;
  firstName: string;
  middleNames: string;
  lastName: string;
  legalUniqueId: string;
  createdDate: string;
  createdByName: string;
  version: string;
}

export interface ILegalEntityContactSimple {
  name?: string;
  role?: string;
  email: string;
  phoneNumber: {
    codeDictionaryUniqueId: string;
    value: string;
  };
}

export interface ILegalEntityGroupFirearm {
  uniqueId: string;
  serialNumber: string;
  make: string;
  model: string;
  caliber: string;
  state: string;
  activityUniqueId: string;
  legalityUniqueId: string;
  stateUniqueId: string;
}

export interface IAssignLE {
  legalUniqueId: string;
  accountId: string | null | undefined;
}

export interface ITransactionType {
  uniqueId: string;
  name: string;
  type: string;
  isActive: string;
}

export interface ILicenceSearchForLeParameters {
  legalUniqueId: string;
  weaponLicenceNumber: string | null;
  statusUniqueId: string | null;
}

export interface IILegalEntityLicenceAttachment {
  uniqueId: string;
  filename: string;
  fileType: string;
  weaponLicenceDocumentTypeUniqueId: string;
  createdDate: string;
  addedBy: string;
}
export interface ILegalEntityLicence {
  uniqueId: string;
  weaponLicenceName: string;
  weaponLicenceNumber: string;
  createdDate: string;
  startDate: string;
  expiryDate: string;
  statusUniqueId: string;
  hasAnyIllegalFirearm: boolean;
  hasAnyInactiveFirearm: boolean;
  hasAnyLeDataChanged: boolean;
  hasAnyFirearmNotAssignedToLegal: boolean;
  hasInactiveOwner: boolean;
  attachments?: IILegalEntityLicenceAttachment[];
  canBeVaried: boolean;
  canBeRenewed: boolean;
  printWarning?: string;
  renewed: boolean;
}

export interface ILegalEntityLicences extends IPaginator {
  content: ILegalEntityLicence[];
}

export interface ILegalEntityApplicationAttachment {
  id: string;
  filename: string;
  type: string;
  createDate: string;
  addedBy: string;
}
export interface ILegalEntityApplication {
  uniqueId: string;
  applicationName: string;
  applicationType: string;
  licenceCode: string;
  status: string;
  createDate: string;
  assignedTo: string;
  attachments: ILegalEntityApplicationAttachment[];
}

export interface ILegalEntityApplications extends IPaginator {
  content: ILegalEntityApplication[];
}

interface IWeaponLicenceGeneralInfo {
  name: string;
  code: string;
  startDate: string;
  expiryDate: string;
  paymentStatusUniqueId: string;
  licenceAuthorityUniqueId?: string;
  secondaryLicenceAuthorityUniqueId?: string;
  applicationProcessUniqueId?: string;
  statusUniqueId?: string;
  licenceNumber?: string;
}

interface IWeaponLicenceConditions {
  weaponNumberLimit: number;
  goodReasonUniqueIds: string[];
  goodReason: string;
  areaOfValidityUniqueId: string;
  competencyUniqueId: string;
  safeStorage: string;
  weaponCategorisationUniqueId: string;
  carry: string;
  additionalConditions: string;
}

interface IPrimaryAddress {
  typeUniqueId: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  regionUniqueId: string;
  countryUniqueId: string;
  latitude: number | string;
  longitude: number | string;
  storage: boolean;
  addressUniqueId: string;
}

interface IWeaponLicenceOwner {
  legalUniqueId: string;
  legalType: LETypes | string;
  individual: IWeaponLicenceOwnerIndividual | null;
  organisation: IWeaponLicenceOwnerOrganisation | null;
  primaryAddress: IPrimaryAddress | null;
}

interface IWeaponLicenceOwnerIndividual {
  name: string;
  genderUniqueId: string;
  dateOfBirth: string;
  placeOfBirth: string;
  nationalityUniqueId: string;
  height: string;
  secondSurname: string;
}

interface IWeaponLicenceOwnerOrganisation {
  organisationName: string;
  organisationCategoryUniqueId: string;
  organisationTypeUniqueIds: string;
}

interface IFirearm {
  firearmLinkToBeRemovedUniqueIds: string[];
  firearmToLinkUniqueIds: string[];
}

export interface ILegalEntityLicenceDetails {
  uniqueId: string;
  canBeVaried: boolean;
  canBeRenewed: boolean;
  renewed: boolean;
  general: IWeaponLicenceGeneralInfo;
  conditions: IWeaponLicenceConditions;
  owner: IWeaponLicenceOwner;
  legal?: ILicenceLegalEntityInfo;
  firearm?: IFirearm;
  version: number;
  additionalInformation?: string;
  lastStatusChangeDescription?: string;
  hasAnyLeDataChanged: boolean;
  hasAnyFirearmNotAssignedToLegal: boolean;
  hasInactiveOwner: boolean;
  createdDate: string;
  weaponLicenceTemplateUniqueId: string;
  galleryEntryUniqueId: string;
}
export interface ILegalEntityLicenceMismatching {
  firstName: boolean;
  middleNames: boolean;
  lastName: boolean;
  secondSurname: boolean;
  documentType: boolean;
  documentNumber: boolean;
  gender: boolean;
  dateOfBirth: boolean;
  placeOfBirth: boolean;
  height: boolean;
  nationality: boolean;
  occupation: boolean;
  organisationName: boolean;
  organisationTypes: boolean;
  departmentName: boolean;
  primaryAddress: boolean;
}
export interface ILicenceLegalEntityInfo {
  uniqueId: string;
  type: LETypes;
  visibilityUniqueId: string;
  prefixGroupUniqueId: string;
}

export interface ILicenceHasSuspendedParent {
  hasSuspendedParent: boolean;
}

export interface IWeaponLicenceGeneralInfoDetailed extends IWeaponLicenceGeneralInfo {
  licenceNumber?: string;
  statusUniqueId?: string;
  status?: string;
  licenceAuthorityUniqueId?: string;
  secondaryLicenceAuthorityUniqueId?: string;
}

export interface IWeaponLicenceConditionsDetailed extends IWeaponLicenceConditions {
  areaOfValidity?: string;
  weaponCategory?: string;
  safeStorageDescription?: string;
  safeStorages?: ILicenceSafeStorage[];
  hasSafeStorage?: boolean;
}

export interface IWeaponLicenceOwnerDetailed extends IWeaponLicenceOwner {
  individual: IWeaponLicenceOwnerIndividualDetailed | null;
  documentType?: string;
  identityDocumentNumber?: string;
  displayUniqueId?: string;
}

export interface IWeaponLicenceOwnerIndividualDetailed extends IWeaponLicenceOwnerIndividual {
  firstName?: string;
  middleNames?: string;
  lastName?: string;
  prefixValueUniqueId?: string;
  identityDocumentTypeUniqueId?: string;
  identityDocumentNumber?: string;
}

export interface IExtendedLegalEntityLicenceDetails {
  canBeVaried: boolean;
  canBeRenewed: boolean;
  renewed: boolean;
  general: IWeaponLicenceGeneralInfoDetailed;
  conditions: IWeaponLicenceConditionsDetailed;
  owner: IWeaponLicenceOwnerDetailed;
  legal?: ILicenceLegalEntityInfo;
  additionalInformation?: string;
  lastStatusChangeDescription?: string;
  hasAnyLeDataChanged: boolean;
  hasAnyFirearmNotAssignedToLegal: boolean;
  hasAnyFirearmIllegal?: boolean;
  hasAnyFirearmInactive?: boolean;
  hasInactiveOwner?: boolean;
  printWarning?: string;
  customFields?: ILegalEntityCustomField[];
}

export interface IExtendedWeaponLicenceOwner extends IWeaponLicenceOwnerDetailed {}
export interface IExtendedWeaponLicenceOwnerIndividual extends IWeaponLicenceOwnerIndividualDetailed {}
export interface IExtendedWeaponLicenceOwnerOrganisation extends IWeaponLicenceOwnerOrganisation {}

export interface ISearchLegalEntityDetailsLicenceValues {
  weaponLicenceName: string;
  weaponLicenceCode: string;
}

export interface IObject {
  [key: string]: boolean;
}

export interface IDocument {
  name: string;
  weaponLicenceDocumentType: string;
  comment: string;
  file: File | null;
}

export interface ILegalEntityLicenceFormValues {
  caseUniqueId: string;
  weaponLicenceTemplateUniqueId: string;
  general: {
    name: string;
    code: string;
    startDate: string;
    expiryDate: string | null;
  };
  conditions: {
    weaponNumberLimit: number | null;
    goodReasonUniqueIds: IObject | null;
    goodReason: string | null;
    areaOfValidityUniqueId?: string | null;
    competencyUniqueId: string | null;
    weaponCategorisationUniqueId: string | null;
    carry: string | null;
    additionalConditions: string | null;
    safeStorageDescription: string | null;
    hasSafeStorage: boolean | string | null;
    safeStorages: any[] | null;
  };
  documents: IDocument[];
  additionalInformation: string | null;
  suitabilityAssessmentPassed?: boolean;
  galleryEntryUniqueId?: string | null;
  customFields: ICustomFieldForm[];
}
export interface ILegalEntityLicenceVaryFormValues {
  uniqueId?: string;
  suitabilityAssessmentPassed?: boolean;
  conditions: {
    weaponNumberLimit: number | null;
    goodReasonUniqueIds: IObject | null;
    goodReason: string;
    areaOfValidityUniqueId?: string | null;
    competencyUniqueId: string | null;
    hasSafeStorage: boolean | string | null;
    safeStorageDescription: string | null;
    safeStorages: any[] | null;
    weaponCategorisationUniqueId: string | null;
    carry: string | null;
    additionalConditions: string | null;
  };
  firearm: {
    firearmToLinkUniqueIds: string[];
    firearmLinkToBeRemovedUniqueIds: string[];
  };
  document: {
    documents: IDocument[];
    documentToRemoveUniqueIds: string[];
  };
  additionalInformation: string | null;
  galleryEntryUniqueId: string | null;
  version: string | number;
  weaponLicenceTemplateUniqueId?: string;
  customFields?: ICustomFieldForm[];
}

export interface ILegalEntityLicenceRenewFormValues {
  uniqueId?: string;
  suitabilityAssessmentPassed?: boolean;
  general?: {
    startDate?: string;
    expiryDate?: string | null;
  };
  conditions: {
    weaponNumberLimit: number | null;
    goodReasonUniqueIds: IObject | null;
    goodReason: string;
    areaOfValidityUniqueId?: string | null;
    competencyUniqueId: string | null;
    hasSafeStorage: boolean | string | null;
    safeStorageDescription: string | null;
    safeStorages: any[] | null;
    weaponCategorisationUniqueId: string | null;
    carry: string | null;
    additionalConditions: string | null;
  };
  firearm: {
    firearmLinkToBeRemovedUniqueIds: string[];
  };
  caseUniqueId: string;
  documents: IDocument[];
  additionalInformation: string | null;
  galleryEntryUniqueId: string | null;
  version: string | number;
  weaponLicenceTemplateUniqueId?: string;
  customFields?: ICustomFieldForm[];
}

export interface IIsAddressLinkedToLicenceResponse {
  linked: boolean;
}

export enum ELegalAttachmentsGroupBy {
  NONE = 'NONE',
  NUMBER = 'NUMBER',
  ATTACHMENT_TYPE = 'ATTACHMENT_TYPE',
}

export interface ILegalAttachmentsSearchRequest {
  legalUniqueId: string;
  createDateFrom?: string | null;
  createDateTo?: string | null;
  groupBy: ELegalAttachmentsGroupBy;
}

export interface ILegalAttachmentItem {
  uniqueId: string;
  documentTypeUniqueId: string;
  fileName: string;
  fileType: string;
  licenceName: string;
  licenceUniqueId: string;
  licenceCode: string;
  caseUniqueId: string;
  parentObjectIdNumber: string; // licenceNumber OR caseNumber
  parentObjectStatusUniqueId: string; // licenceStatusUniqueId OR caseStatusUniqueId
  addedBy: string;
  createdDate: string;
}

export type ILegalAttachmentsSearch =
  { items: ILegalAttachmentItem[] }[] | ILegalAttachmentItem[];

export interface ILegalAttachmentsSearchResponse extends IPaginator {
  content: ILegalAttachmentsSearch;
}
export interface IFormDeactivationLEValues {
  reasonUniqueId: string | null;
  version: string;
}
export interface IFormActivationLEValues {
  version: string;
}

export interface ILegalEntityDeactivationWarnings {
  hasAssignedAssets: boolean;
  hasActiveLicence: boolean;
}
